import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";

export default function RidersPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    window.scrollTo(0, 0);
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero Section - Full Bleed */}
        <section className="relative w-full min-h-[90vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cameroonian%20motorcycle%20delivery%20riders%20group%20on%20Douala%20street%20wearing%20colorful%20helmets%20confident%20poses%20warm%20golden%20sunset%20lighting%20urban%20African%20cityscape%20background%20authentic%20atmosphere%20cinematic%20wide%20shot%20professional%20editorial%20photography&width=1600&height=900&seq=riders-hero-full-v2&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-foreground-950/80"></div>
          </div>

          <div className="relative w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-16 md:pb-20">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-400/30 text-accent-200 text-xs font-semibold uppercase tracking-wider mb-6">
                {t("nav.riders")}
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-background-50 mb-6 leading-tight tracking-tight">
                {t("riders.title")}
              </h1>
              <p className="text-lg md:text-xl text-background-200/90 leading-relaxed mb-10 max-w-2xl mx-auto">
                {t("riders.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/waitlist"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all hover:scale-105 shadow-lg shadow-primary-500/25 whitespace-nowrap cursor-pointer"
                >
                  {t("riders.cta")}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
                <a
                  href="https://wa.me/237681172545"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-background-50/10 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-full text-sm font-semibold hover:bg-background-50/20 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-line"></i>
                  </div>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Strip */}
        <section className="relative -mt-16 pb-0">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
            <div className="bg-background-50 rounded-2xl shadow-lg border border-background-200 p-6 md:p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { number: "200+", label: "Livreurs actifs" },
                  { number: "24h", label: "Pour demarrer" },
                  { number: "100%", label: "Horaires flexibles" },
                  { number: "2", label: "Villes" },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">
                      {stat.number}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Deliver With Mayouk */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-14 md:mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold uppercase tracking-wider mb-5">
                Avantages
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5">
                Pourquoi livrer avec Mayouk ?
              </h2>
              <div className="w-20 h-1 bg-primary-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {[
                {
                  icon: "ri-time-line",
                  title: t("riders.benefit1_title"),
                  desc: t("riders.benefit1_desc"),
                  img: "https://readdy.ai/api/search-image?query=Camaroonian%20motorcycle%20rider%20relaxing%20leaning%20on%20bike%20smiling%20confident%20expression%20warm%20sunset%20Douala%20street%20background%20casual%20authentic%20scene%20golden%20hour%20lighting%20editorial%20photography&width=600&height=400&seq=rider-flex-v2&orientation=landscape"
                },
                {
                  icon: "ri-route-line",
                  title: t("riders.benefit2_title"),
                  desc: t("riders.benefit2_desc"),
                  img: "https://readdy.ai/api/search-image?query=Motorcycle%20delivery%20rider%20navigating%20busy%20Douala%20street%20with%20food%20delivery%20backpack%20colorful%20urban%20background%20authentic%20Cameroonian%20city%20scene%20warm%20lighting%20dynamic%20composition%20editorial%20photography&width=600&height=400&seq=rider-opps-v2&orientation=landscape"
                },
                {
                  icon: "ri-coins-line",
                  title: t("riders.benefit3_title"),
                  desc: t("riders.benefit3_desc"),
                  img: "https://readdy.ai/api/search-image?query=Cameroonian%20man%20counting%20CFA%20francs%20money%20on%20motorcycle%20seat%20warm%20lighting%20positive%20expression%20successful%20delivery%20rider%20concept%20authentic%20local%20scene%20editorial%20photography&width=600&height=400&seq=rider-income-v2&orientation=landscape"
                },
                {
                  icon: "ri-user-add-line",
                  title: t("riders.benefit4_title"),
                  desc: t("riders.benefit4_desc"),
                  img: "https://readdy.ai/api/search-image?query=Cameroonian%20young%20man%20filling%20out%20form%20on%20smartphone%20sitting%20on%20motorcycle%20friendly%20genuine%20smile%20warm%20natural%20lighting%20urban%20Douala%20background%20authentic%20scene%20editorial%20photography&width=600&height=400&seq=rider-signup-v2&orientation=landscape"
                },
              ].map((item, index) => (
                <div key={index} className="group bg-background-100 rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-500 hover:shadow-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="h-48 sm:h-full overflow-hidden">
                      <img
                        src={item.img}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6 md:p-7 flex flex-col justify-center">
                      <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${item.icon} text-primary-600 text-lg`}></i>
                        </div>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-14 md:mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold uppercase tracking-wider mb-5">
                Processus
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5">
                Comment ca marche
              </h2>
              <div className="w-20 h-1 bg-accent-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  step: "1",
                  title: "Inscrivez-vous",
                  desc: "Enregistrez-vous avec votre numero de telephone et les details de votre moto en quelques minutes.",
                  img: "https://readdy.ai/api/search-image?query=Cameroonian%20person%20registering%20on%20smartphone%20mobile%20app%20signup%20form%20warm%20indoor%20lighting%20friendly%20modern%20setting%20authentic%20scene%20editorial%20photography&width=500&height=500&seq=rider-step1-v2&orientation=squarish"
                },
                {
                  step: "2",
                  title: "Verification rapide",
                  desc: "Notre equipe verifie votre profil en moins de 24h pour que vous puissiez commencer.",
                  img: "https://readdy.ai/api/search-image?query=Smartphone%20screen%20showing%20verification%20approved%20checkmark%20notification%20modern%20clean%20interface%20Cameroonian%20context%20warm%20lighting%20editorial%20product%20photography&width=500&height=500&seq=rider-step2-v2&orientation=squarish"
                },
                {
                  step: "3",
                  title: "Acceptez les commandes",
                  desc: "Recevez les demandes de livraison pres de chez vous et choisissez celles qui vous arrangent.",
                  img: "https://readdy.ai/api/search-image?query=Cameroonian%20motorcycle%20rider%20looking%20at%20smartphone%20receiving%20delivery%20notification%20on%20busy%20Douala%20street%20warm%20golden%20hour%20lighting%20authentic%20urban%20scene%20editorial%20photography&width=500&height=500&seq=rider-step3-v2&orientation=squarish"
                },
                {
                  step: "4",
                  title: "Soyez paye",
                  desc: "Recevez votre argent directement sur votre compte Mobile Money apres chaque livraison.",
                  img: "https://readdy.ai/api/search-image?query=Mobile%20Money%20payment%20confirmation%20on%20smartphone%20screen%20MTN%20Orange%20Money%20Cameroon%20happy%20expression%20warm%20lighting%20modern%20setting%20editorial%20photography&width=500&height=500&seq=rider-step4-v2&orientation=squarish"
                },
              ].map((item, index) => (
                <div key={index} className="group relative">
                  <div className="absolute -top-3 -left-3 z-20 w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center shadow-lg">
                    <span className="text-sm font-bold text-background-50">{item.step}</span>
                  </div>
                  <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200 hover:border-accent-200 transition-all duration-500 hover:shadow-lg h-full">
                    <div className="h-44 overflow-hidden">
                      <img
                        src={item.img}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cameroonian%20motorcycle%20riders%20group%20on%20Douala%20road%20sunset%20warm%20golden%20light%20silhouettes%20against%20colorful%20sky%20sense%20of%20community%20and%20freedom%20authentic%20local%20atmosphere%20cinematic%20wide%20shot%20editorial%20photography&width=1600&height=600&seq=riders-cta-bg-v2&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-foreground-950/80"></div>
          </div>
          <div className="relative w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-5">
              Pret a commencer a gagner ?
            </h2>
            <p className="text-base md:text-lg text-background-300 mb-8 max-w-xl mx-auto">
              Rejoignez le reseau de livreurs Mayouk et transformez vos trajets quotidiens en revenus supplementaires.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/waitlist"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all hover:scale-105 shadow-lg shadow-accent-500/25 whitespace-nowrap cursor-pointer"
              >
                {t("riders.cta")}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
              <a
                href="https://wa.me/237681172545"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-background-50/10 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-full text-sm font-semibold hover:bg-background-50/20 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line"></i>
                </div>
                Nous contacter
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}